/**
 * Tenancy scopes for the Transactions explorer.
 *
 * The same on-chain transaction is stored once per tenancy tier (user-private,
 * tenant-shared, org, customer, global) — `transactions-mgmt-api`'s
 * `GET /transactions` re-resolves which partition it reads from the tenancy
 * scope-widen query params (`customer_id` / `org_id` / `tenant_id` / `user_id`),
 * the SAME params the single-tx detail switcher sends (see
 * `commons.aws.dynamodb.queries.canonical_resolution._overrides_for`). This
 * module is the list-level counterpart: a fixed scope ladder + the override
 * params each one maps to, so the explorer can show "my / tenant / org / global"
 * feeds without forking the backend's scope vocabulary.
 *
 * Two of the scopes are special:
 *  - `user` widens DOWN to the caller's own per-user feed, which the list
 *    endpoint only serves when an explicit `user_id` is sent (the default feed
 *    is the cross-user tenant partition) — so we stamp the caller's id;
 *  - `latest` is NOT a persisted DDB scope at all: it's the ephemeral
 *    latest-block blockchain feed (`/transactions/blockchain`), surfaced here as
 *    a sibling option per the product's "global = system-wide latest blocks".
 */

/** Shared-tier sentinel (`commons.tenancy.GLOBAL_TENANT`). */
const GLOBAL_TENANT = "main";

export type TxScopeId = "user" | "tenant" | "org" | "customer" | "global" | "latest";

export interface TxScopeDef {
  id: TxScopeId;
  /** Short toolbar label. */
  label: string;
  /** One-line helper shown under the toolbar / in the empty state. */
  description: string;
  /**
   * `live`: the ephemeral latest-block feed (no persisted scope params).
   * `persisted`: a DynamoDB tier read via scope-widen overrides.
   */
  kind: "persisted" | "live";
}

export const TX_SCOPES: readonly TxScopeDef[] = [
  {
    id: "user",
    label: "My transactions",
    description: "Transactions saved to your own private workspace.",
    kind: "persisted",
  },
  {
    id: "tenant",
    label: "Tenant",
    description: "Everything shared across your current tenant.",
    kind: "persisted",
  },
  {
    id: "org",
    label: "Organization",
    description: "The organization-level feed, shared across this org's tenants.",
    kind: "persisted",
  },
  {
    id: "customer",
    label: "Customer",
    description: "The customer-level feed, shared across the whole account.",
    kind: "persisted",
  },
  {
    id: "global",
    label: "Global",
    description: "The shared global record of on-chain transactions, system-wide.",
    kind: "persisted",
  },
  {
    id: "latest",
    label: "Latest blocks",
    description: "Live transactions from the latest block on each preview chain.",
    kind: "live",
  },
] as const;

/**
 * The product default feed scope — tenant-shared, the sensible landing tier for
 * a shared investigation. The user-editable default (Settings › Default view
 * scope) falls back to this. Also the safe default for an unknown scope id.
 */
export const DEFAULT_TX_SCOPE_ID: TxScopeId = "tenant";

/**
 * The persisted TENANCY tiers only (drops the ephemeral `latest` block feed),
 * for the Settings default-scope picker: a default of "latest" makes no sense as
 * a tenancy tier.
 */
export const TX_TENANCY_SCOPES: readonly TxScopeDef[] = TX_SCOPES.filter(
  (s) => s.kind === "persisted",
);

/**
 * A tenancy tier only. What anything *stored* may hold — the Settings default
 * scope, an investigation's feed tier — since `latest` names an endpoint rather
 * than a partition and cannot be resolved to scope-widen params.
 */
export type TxTenancyScopeId = Exclude<TxScopeId, "latest">;

/** Narrow a persisted / hand-edited value, so an unknown tier falls back. */
export function isTxTenancyScopeId(value: unknown): value is TxTenancyScopeId {
  return TX_TENANCY_SCOPES.some((s) => s.id === value);
}

export function txScope(id: TxScopeId): TxScopeDef {
  return TX_SCOPES.find((s) => s.id === id) ?? (TX_SCOPES[1] as TxScopeDef);
}

/**
 * Tenancy scope-widen query params for a PERSISTED scope, mirroring the
 * backend's `_overrides_for`. `tenant` is the default cross-user partition (no
 * override); `user` needs the caller's id stamped (the list endpoint routes an
 * explicit `user_id` to that user's own feed). Returns `{}` for `latest` (it
 * uses a different endpoint entirely).
 */
export function scopeQueryParams(
  scope: TxScopeId,
  callerUserId: string | null | undefined,
): Record<string, string> {
  switch (scope) {
    case "user":
      // Explicit per-user feed; without a known caller id, fall back to tenant.
      return callerUserId ? { user_id: callerUserId } : {};
    case "org":
      return { tenant_id: GLOBAL_TENANT };
    case "customer":
      return { org_id: GLOBAL_TENANT, tenant_id: GLOBAL_TENANT };
    case "global":
      return { customer_id: GLOBAL_TENANT, org_id: GLOBAL_TENANT, tenant_id: GLOBAL_TENANT };
    case "tenant":
    case "latest":
    default:
      return {};
  }
}
